import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Building, DollarSign, TrendingUp, FileText, Wrench, Users, BarChart3, Upload, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, [id]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/portfolio/holdings/${id}/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching property profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile || profile.error) {
    return (
      <div className="p-6 lg:p-8">
        <Button variant="ghost" onClick={() => navigate('/portfolio')} className="mb-4">
          <ArrowLeft size={16} className="mr-2" /> Back to Portfolio
        </Button>
        <Card className="border-border">
          <CardContent className="py-16 text-center">
            <Building size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">Property not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { general_info, financial_dashboard, kpis, documents, maintenance, tenants, transactions } = profile;

  return (
    <div className="p-6 lg:p-8" data-testid="property-profile-page">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/portfolio')} className="mb-4">
          <ArrowLeft size={16} className="mr-2" /> Back to Portfolio
        </Button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight mb-2">{general_info?.address}</h1>
            <div className="flex items-center gap-3">
              <Badge className="bg-primary">{general_info?.asset_class}</Badge>
              <Badge variant="outline">{general_info?.deal_type}</Badge>
              {general_info?.apn && <span className="text-sm text-muted-foreground">APN: {general_info.apn}</span>}
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Current Value</p>
            <p className="text-3xl font-bold font-mono text-primary">${(general_info?.current_value || 0).toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Monthly Cash Flow</p>
            <p className={`text-xl font-bold font-mono ${(kpis?.monthly_cashflow || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              ${(kpis?.monthly_cashflow || 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Equity</p>
            <p className="text-xl font-bold font-mono text-accent">${(kpis?.equity || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Cash-on-Cash</p>
            <p className="text-xl font-bold font-mono text-primary">{kpis?.cash_on_cash_return || 0}%</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Cap Rate</p>
            <p className="text-xl font-bold font-mono">{kpis?.cap_rate || 0}%</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">DSCR</p>
            <p className="text-xl font-bold font-mono">{kpis?.dscr || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">ROI Since Purchase</p>
            <p className={`text-xl font-bold font-mono ${(kpis?.roi_since_purchase || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {kpis?.roi_since_purchase || 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="financial" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="financial" className="flex items-center gap-2">
            <DollarSign size={16} /> Financial
          </TabsTrigger>
          <TabsTrigger value="tenants" className="flex items-center gap-2">
            <Users size={16} /> Tenants ({tenants?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="flex items-center gap-2">
            <Wrench size={16} /> Maintenance ({maintenance?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <FileText size={16} /> Documents ({documents?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Financial Tab */}
        <TabsContent value="financial">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Income Breakdown */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-green-400 flex items-center gap-2">
                  <TrendingUp size={18} /> Income
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Rent Received</span>
                  <span className="font-mono text-green-400">${(financial_dashboard?.income?.rent_received || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Section 8 Payments</span>
                  <span className="font-mono text-green-400">${(financial_dashboard?.income?.section8_payments || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Late Fees</span>
                  <span className="font-mono">${(financial_dashboard?.income?.late_fees || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Other Income</span>
                  <span className="font-mono">${(financial_dashboard?.income?.other_income || 0).toLocaleString()}</span>
                </div>
                <div className="border-t border-border pt-3 flex justify-between font-semibold">
                  <span>Total Income</span>
                  <span className="font-mono text-green-400">${(financial_dashboard?.income?.total || 0).toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>

            {/* Expense Breakdown */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-red-400 flex items-center gap-2">
                  <TrendingUp size={18} className="rotate-180" /> Expenses
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Mortgage/Loan</span>
                  <span className="font-mono text-red-400">${(financial_dashboard?.expenses?.mortgage_payment || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Property Tax</span>
                  <span className="font-mono">${(financial_dashboard?.expenses?.property_tax || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Insurance</span>
                  <span className="font-mono">${(financial_dashboard?.expenses?.insurance || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Repairs</span>
                  <span className="font-mono">${(financial_dashboard?.expenses?.repairs || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Utilities</span>
                  <span className="font-mono">${(financial_dashboard?.expenses?.utilities || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">CapEx</span>
                  <span className="font-mono">${(financial_dashboard?.expenses?.capex || 0).toLocaleString()}</span>
                </div>
                <div className="border-t border-border pt-3 flex justify-between font-semibold">
                  <span>Total Expenses</span>
                  <span className="font-mono text-red-400">${(financial_dashboard?.expenses?.total || 0).toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>

            {/* Debt Tracking */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 size={18} /> Debt
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Mortgage Balance</span>
                  <span className="font-mono">${(financial_dashboard?.debt?.mortgage_balance || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Monthly Payment</span>
                  <span className="font-mono">${(financial_dashboard?.debt?.monthly_payment || 0).toLocaleString()}</span>
                </div>
                {financial_dashboard?.debt?.mortgage && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Interest Rate</span>
                      <span className="font-mono">{financial_dashboard.debt.mortgage.interest_rate}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Lender</span>
                      <span className="font-mono text-sm">{financial_dashboard.debt.mortgage.lender_name}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Cash Flow Trend */}
          <Card className="border-border mt-6">
            <CardHeader>
              <CardTitle>12-Month Cash Flow Trend</CardTitle>
            </CardHeader>
            <CardContent>
              {financial_dashboard?.cashflow?.trend?.length > 0 ? (
                <div className="space-y-2">
                  {financial_dashboard.cashflow.trend.map((month, index) => (
                    <div key={month.month || `month-${index}`} className="flex items-center gap-4">
                      <span className="w-20 font-mono text-sm text-muted-foreground">{month.month}</span>
                      <div className="flex-1 flex items-center gap-2">
                        <div className="h-6 bg-green-500/30 rounded" style={{width: `${Math.min(month.income / 50, 100)}%`}}></div>
                        <span className="text-xs text-green-400">${month.income?.toLocaleString()}</span>
                      </div>
                      <div className="flex-1 flex items-center gap-2">
                        <div className="h-6 bg-red-500/30 rounded" style={{width: `${Math.min(month.expenses / 50, 100)}%`}}></div>
                        <span className="text-xs text-red-400">${month.expenses?.toLocaleString()}</span>
                      </div>
                      <span className={`w-24 text-right font-mono ${month.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        ${month.net?.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No cash flow data available</p>
              )}
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          <Card className="border-border mt-6">
            <CardHeader>
              <CardTitle>Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              {transactions?.length > 0 ? (
                <div className="space-y-2">
                  {transactions.slice(0, 10).map((tx, index) => (
                    <div key={tx.id || `tx-${index}`} className="flex items-center justify-between p-2 bg-secondary/30 rounded">
                      <div className="flex items-center gap-3">
                        <Calendar size={14} className="text-muted-foreground" />
                        <span className="text-sm">{tx.date?.substring(0, 10)}</span>
                        <Badge variant="outline" className="text-xs">{tx.type}</Badge>
                      </div>
                      <span className={`font-mono ${tx.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {tx.amount >= 0 ? '+' : ''}${tx.amount?.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No transactions recorded</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tenants Tab */}
        <TabsContent value="tenants">
          {tenants?.length === 0 ? (
            <Card className="border-border">
              <CardContent className="py-16 text-center">
                <Users size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No tenants</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {tenants.map((tenant) => (
                <Card key={tenant.id} className="border-border">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                          <Users className="text-primary" size={24} />
                        </div>
                        <div>
                          <p className="font-semibold">{tenant.name}</p>
                          <p className="text-sm text-muted-foreground">{tenant.email}</p>
                          <p className="text-xs text-muted-foreground">Unit: {tenant.unit_number || 'Main'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-bold text-primary">${tenant.monthly_rent?.toLocaleString()}/mo</p>
                        {tenant.section8 && <Badge className="bg-purple-500">Section 8</Badge>}
                        <p className="text-xs text-muted-foreground mt-1">
                          Lease: {tenant.lease_start} - {tenant.lease_end}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Maintenance Tab */}
        <TabsContent value="maintenance">
          {maintenance?.length === 0 ? (
            <Card className="border-border">
              <CardContent className="py-16 text-center">
                <Wrench size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No maintenance tickets</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {maintenance.map((ticket) => (
                <Card key={ticket.id} className="border-border border-l-4" style={{borderLeftColor: ticket.priority === 'urgent' ? '#ef4444' : ticket.priority === 'high' ? '#f97316' : '#3b82f6'}}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Wrench size={16} />
                          <span className="font-semibold capitalize">{ticket.issue_type}</span>
                          <Badge variant="outline">{ticket.status}</Badge>
                          <Badge variant="secondary">{ticket.priority}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{ticket.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">Created: {ticket.created_at?.substring(0, 10)}</p>
                      </div>
                      {ticket.actual_cost && (
                        <span className="font-mono text-red-400">${ticket.actual_cost?.toLocaleString()}</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <div className="flex justify-end mb-4">
            <Button className="bg-accent hover:bg-accent/90">
              <Upload size={16} className="mr-2" /> Upload Document
            </Button>
          </div>
          {documents?.length === 0 ? (
            <Card className="border-border">
              <CardContent className="py-16 text-center">
                <FileText size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No documents uploaded</p>
                <p className="text-sm text-muted-foreground mt-2">Upload contracts, statements, appraisals, and more</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.map((doc) => (
                <Card key={doc.id} className="border-border cursor-pointer hover:border-primary">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      <FileText size={24} className="text-primary" />
                      <div>
                        <p className="font-semibold">{doc.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{doc.type}</p>
                        <p className="text-xs text-muted-foreground">{doc.uploaded_at?.substring(0, 10)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PropertyProfile;
